import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

export const Preloader = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const timers = [];

    // Phase 1: Show company name
    timers.push(setTimeout(() => setPhase(1), 600));
    
    // Phase 2: Progress bar filling  
    timers.push(setTimeout(() => setPhase(2), 1800));
    
    // Phase 3: Complete and call onComplete
    timers.push(setTimeout(() => {
      setPhase(3);
    }, 2600));

    return () => {
      timers.forEach(t => clearTimeout(t));
    };
  }, []);

  // Call onComplete when phase reaches 3
  useEffect(() => {
    if (phase === 3 && onComplete) {
      onComplete();
    }
  }, [phase, onComplete]);

  return (
    <AnimatePresence>
      {phase < 3 && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ background: "#0F1225" }}
          data-testid="preloader"
        >
          {/* Grid background */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: "linear-gradient(rgba(123,104,238,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(123,104,238,0.03) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />

          {/* Glow */}
          <div
            className="absolute pointer-events-none"
            style={{
              width: "400px",
              height: "400px",
              background: "radial-gradient(circle, rgba(123,104,238,0.12) 0%, transparent 70%)",
              filter: "blur(60px)",
            }}
          />

          <div className="flex flex-col items-center gap-8">
            {/* Logo */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
              className="w-20 h-20 flex items-center justify-center rounded-2xl"
              style={{ background: "linear-gradient(135deg, #7B68EE, #5B4BD6)" }}
            >
              <span
                className="text-white font-bold"
                style={{ fontFamily: "'Kode Mono', monospace", fontSize: "36px" }}
              >
                M
              </span>
            </motion.div>

            {/* Company Name */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={phase >= 1 ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
              className="flex flex-col items-center gap-3"
            >
              <h1
                style={{
                  fontFamily: "'Kode Mono', monospace",
                  fontSize: "28px",
                  fontWeight: 600,
                  color: "#F0F0FF",
                  letterSpacing: "-0.02em",
                }}
              >
                Manchi Group
              </h1>
              <div className="flex items-center gap-3">
                <div className="w-6 h-[1px]" style={{ background: "#7B68EE" }} />
                <span
                  style={{
                    fontFamily: "'Kode Mono', monospace",
                    fontSize: "12px",
                    color: "#7B68EE",
                    textTransform: "uppercase",
                    letterSpacing: "0.2em",
                  }}
                >
                  Innovation. Technology. Excellence.
                </span>
                <div className="w-6 h-[1px]" style={{ background: "#7B68EE" }} />
              </div>
            </motion.div>

            {/* Loading bar */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={phase >= 1 ? { opacity: 1 } : {}}
              transition={{ duration: 0.3 }}
              className="w-48 h-[2px] rounded-full overflow-hidden"
              style={{ background: "rgba(123,104,238,0.15)" }}
            >
              <motion.div
                initial={{ width: "0%" }}
                animate={phase >= 1 ? { width: "100%" } : {}}
                transition={{ duration: 1.6, ease: "easeInOut" }}
                className="h-full rounded-full"
                style={{ background: "linear-gradient(90deg, #7B68EE, #60A5FA)" }}
              />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
